import { useMemo } from 'react';

import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import type {
  ClusterRestrictionDetails,
  ClusterRestrictionDetailsManifest,
  ClusterRestrictionDetailsRestriction,
} from '@cast/types';

import {
  formatIssueDescription,
  formatIssueReason,
  formatRestrictionType,
} from '../_utils/formatRestrictions';
import { addLineRangeToSet } from '../_utils/selectionUtils';
import type { RestrictionCategory, YamlFile } from '../types';

const toManifestKey = (manifestId?: {
  resourceNamespace?: string;
  resourceKind?: string;
  resourceName?: string;
}) =>
  manifestId
    ? `${manifestId.resourceNamespace || ''}/${manifestId.resourceKind || ''}/${
        manifestId.resourceName || ''
      }`
    : undefined;

const getManifestId = (manifest: ClusterRestrictionDetailsManifest) => {
  return toManifestKey(manifest.id) || manifest.displayName || 'manifest.yaml';
};

const getManifestName = (manifest: ClusterRestrictionDetailsManifest) => {
  if (manifest.displayName) return manifest.displayName;
  if (manifest.id?.resourceKind) return `${manifest.id.resourceKind}.yaml`;
  return 'manifest.yaml';
};

const getFirstManifestId = (
  restrictions: ClusterRestrictionDetailsRestriction[]
) => {
  const firstIssue = restrictions[0]?.issues?.[0];
  return toManifestKey(firstIssue?.manifestId) || '';
};

export const useRestrictionDetailsData = (
  data: ClusterRestrictionDetails | undefined
) => {
  return useMemo(() => {
    if (!data) return { categories: [], yamlFiles: [] };

    const restrictions = data.restrictions || [];
    const manifests = data.manifests || [];

    // Group restrictions by type and build categories
    const restrictionsByType = groupBy(restrictions, (r) => r.type || 'Other');

    const categories: RestrictionCategory[] = Object.entries(restrictionsByType)
      .sort(([typeA, restrictionsA], [typeB, restrictionsB]) => {
        const manifestCompare = getFirstManifestId(restrictionsA).localeCompare(
          getFirstManifestId(restrictionsB)
        );
        return manifestCompare !== 0
          ? manifestCompare
          : formatRestrictionType(typeA).localeCompare(
              formatRestrictionType(typeB)
            );
      })
      .map(([type, typeRestrictions]) => {
        const issues = sortBy(
          typeRestrictions.flatMap((r) => r.issues || []),
          [
            (issue) => toManifestKey(issue.manifestId) || '',
            (issue) => issue.startLine ?? 0,
          ]
        );

        return {
          title: formatRestrictionType(type),
          restrictionType: type,
          items: issues.map((issue, idx) => {
            const manifestId = toManifestKey(issue.manifestId);

            return {
              title: formatIssueReason(
                issue.reason || '',
                issue.additionalContext
              ),
              description: formatIssueDescription(issue, type),
              manifestId,
              startLine: issue.startLine,
              endLine: issue.endLine,
              itemId: `${type}-${idx}`,
            };
          }),
        };
      });

    // Build highlighted lines map
    const highlightedLinesByManifest = new Map<string, Set<number>>();

    restrictions.forEach((restriction) => {
      restriction.issues?.forEach((issue) => {
        const manifestId = toManifestKey(issue.manifestId);
        if (!manifestId) return;

        const startLine = issue.startLine;
        const endLine = issue.endLine;

        if (startLine === undefined || endLine === undefined) return;

        if (!highlightedLinesByManifest.has(manifestId)) {
          highlightedLinesByManifest.set(manifestId, new Set());
        }
        addLineRangeToSet(
          highlightedLinesByManifest.get(manifestId)!,
          startLine,
          endLine
        );
      });
    });

    // Build YAML files
    const yamlFiles: YamlFile[] = manifests.map((manifest) => {
      const manifestId = getManifestId(manifest);
      const highlightedLines = highlightedLinesByManifest.get(manifestId);

      return {
        id: manifestId,
        name: getManifestName(manifest),
        data: manifest.manifestYaml || '',
        highlightedLines: highlightedLines
          ? Array.from(highlightedLines).sort((a, b) => a - b)
          : undefined,
      };
    });

    return { categories, yamlFiles };
  }, [data]);
};
